import { Event, request } from '@mirai/data-sources';
import { useCallback, useRef } from 'react';

import { EVENT, METRICS_EVENTS } from '../../../../helpers';
import { getClientFingerprint } from '../../../helpers';
import { getAllowedOpenUrl, getAllowedOpenUrlPrefix } from '../helpers';

const createMcpState = () => ({
  pending: false,
  pendingCallIds: new Set(),
  pendingCount: 0,
  handledCallIds: new Set(),
  handledApprovalIds: new Set(),
  failedCallIds: new Set(),
  responseIds: new Set(),
  responsesDone: new Set(),
  responsesWithMessage: new Set(),
  resumeRequestedCallIds: new Set(),
  resumeRequestedAny: false,
  retry: { count: 0, timeoutId: null },
});

const createResponseState = () => ({
  activeId: null,
  audioResponseIds: new Set(),
});

const createFunctionState = () => ({
  pendingCalls: new Map(),
  pendingFollowup: false,
  pendingFollowupReason: null,
  pendingFollowupResponse: null,
  handledOpenUrlCalls: new Set(),
});

const useOpenAiRealtimeSession = () => {
  const audioElementRef = useRef(null);
  const audioContextRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const microphoneEnabledRef = useRef(false);
  const pendingIntroRef = useRef(false);
  const introResponseIdRef = useRef(null);
  const mcpStateRef = useRef(createMcpState());
  const responseStateRef = useRef(createResponseState());
  const functionStateRef = useRef(createFunctionState());
  const pendingUserTranscriptRef = useRef('');
  const inputSourceRef = useRef(null);
  const outputSourceRef = useRef(null);
  const inputFrequencyDataRef = useRef(null);
  const outputFrequencyDataRef = useRef(null);
  const audioAnalyzersRef = useRef({
    inputAnalyser: null,
    outputAnalyser: null,
    getInputByteFrequencyData: () => null,
    getOutputByteFrequencyData: () => null,
  });

  const cleanupRealtimeSession = useCallback((shouldSignal = false) => {
    const dataChannel = dataChannelRef.current;
    if (dataChannel) {
      if (shouldSignal && dataChannel.readyState === 'open') {
        dataChannel.send(JSON.stringify({ type: 'session.close' }));
      }
      dataChannel.close();
      dataChannelRef.current = null;
    }

    const peerConnection = peerConnectionRef.current;
    if (peerConnection) {
      peerConnection.getSenders().forEach((sender) => {
        if (sender.track) sender.track.stop();
      });
      peerConnection.close();
      peerConnectionRef.current = null;
    }

    const localStream = localStreamRef.current;
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    localAudioTrackRef.current = null;
    microphoneEnabledRef.current = false;
    pendingIntroRef.current = false;
    introResponseIdRef.current = null;
    if (mcpStateRef.current.retry.timeoutId) {
      clearTimeout(mcpStateRef.current.retry.timeoutId);
    }
    mcpStateRef.current = createMcpState();
    responseStateRef.current = createResponseState();
    functionStateRef.current = createFunctionState();

    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }

    if (inputSourceRef.current) {
      inputSourceRef.current.disconnect();
      inputSourceRef.current = null;
    }

    if (outputSourceRef.current) {
      outputSourceRef.current.disconnect();
      outputSourceRef.current = null;
    }

    audioAnalyzersRef.current.inputAnalyser = null;
    audioAnalyzersRef.current.outputAnalyser = null;
    audioAnalyzersRef.current.getInputByteFrequencyData = () => null;
    audioAnalyzersRef.current.getOutputByteFrequencyData = () => null;
    inputFrequencyDataRef.current = null;
    outputFrequencyDataRef.current = null;

    const audioContext = audioContextRef.current;
    if (audioContext) {
      audioContext.close();
      audioContextRef.current = null;
    }

    pendingUserTranscriptRef.current = '';
  }, []);

  const safeJsonParse = useCallback((value) => {
    if (typeof value !== 'string') return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }, []);

  const enableMicrophone = useCallback(() => {
    const track = localAudioTrackRef.current;
    if (!track || microphoneEnabledRef.current) return;
    track.enabled = true;
    microphoneEnabledRef.current = true;
  }, []);

  const clearPendingMcp = useCallback(() => {
    if (mcpStateRef.current.retry.timeoutId) {
      clearTimeout(mcpStateRef.current.retry.timeoutId);
    }
    mcpStateRef.current = createMcpState();
  }, []);

  const clearPendingFollowup = useCallback(() => {
    functionStateRef.current.pendingFollowup = false;
    functionStateRef.current.pendingFollowupReason = null;
    functionStateRef.current.pendingFollowupResponse = null;
  }, []);

  const sendResponseCreate = useCallback((reason, responseOverrides) => {
    if (responseStateRef.current.activeId || responseStateRef.current.audioResponseIds.size > 0) {
      functionStateRef.current.pendingFollowup = true;
      functionStateRef.current.pendingFollowupReason = reason || null;
      functionStateRef.current.pendingFollowupResponse = responseOverrides || null;
      return false;
    }
    const channel = dataChannelRef.current;
    if (channel && channel.readyState === 'open') {
      const responsePayload = responseOverrides
        ? { type: 'response.create', response: responseOverrides }
        : { type: 'response.create' };
      channel.send(JSON.stringify(responsePayload));
      return true;
    }
    return false;
  }, []);

  const sendPendingFollowup = useCallback(() => {
    if (
      responseStateRef.current.activeId ||
      responseStateRef.current.audioResponseIds.size > 0 ||
      !functionStateRef.current.pendingFollowup
    ) {
      return false;
    }

    const reason = functionStateRef.current.pendingFollowupReason;
    const responseOverrides = functionStateRef.current.pendingFollowupResponse;
    clearPendingFollowup();
    return sendResponseCreate(reason || 'followup', responseOverrides);
  }, [clearPendingFollowup, sendResponseCreate]);

  const requestFollowupResponse = useCallback(
    (reason, outputCallId, responseOverrides) => {
      if (outputCallId && mcpStateRef.current.resumeRequestedCallIds.has(outputCallId)) return false;
      if (!outputCallId && mcpStateRef.current.resumeRequestedAny) return false;
      mcpStateRef.current.resumeRequestedAny = true;
      if (outputCallId) mcpStateRef.current.resumeRequestedCallIds.add(outputCallId);
      return sendResponseCreate(reason || 'followup', responseOverrides);
    },
    [sendResponseCreate],
  );

  const resumeMcpIfNeeded = useCallback(
    (outputCallId) => {
      if (outputCallId) {
        mcpStateRef.current.pendingCallIds.delete(outputCallId);
      }
      if (
        mcpStateRef.current.pending &&
        mcpStateRef.current.pendingCount === 0 &&
        !mcpStateRef.current.resumeRequestedAny &&
        !functionStateRef.current.pendingFollowup
      ) {
        clearPendingMcp();
      }
    },
    [clearPendingMcp],
  );

  const trackMcpStart = useCallback((callId) => {
    mcpStateRef.current.pending = true;
    if (callId) {
      if (mcpStateRef.current.handledCallIds.has(callId)) return;
      if (mcpStateRef.current.pendingCallIds.has(callId)) return;
      mcpStateRef.current.pendingCallIds.add(callId);
      mcpStateRef.current.pendingCount += 1;
      return;
    }
    mcpStateRef.current.pendingCount += 1;
  }, []);

  const getFunctionCallKey = useCallback((callId, responseId, name) => {
    if (callId) return callId;
    if (responseId && name) return `${responseId}:${name}`;
    return null;
  }, []);

  const upsertFunctionCall = useCallback(({ key, name, responseId, callId, args }) => {
    if (!key) return;
    const existing = functionStateRef.current.pendingCalls.get(key) || { key };
    functionStateRef.current.pendingCalls.set(key, {
      ...existing,
      name: name ?? existing.name,
      responseId: responseId ?? existing.responseId,
      callId: callId ?? existing.callId,
      args: args ?? existing.args,
    });
  }, []);

  const handleMcpCompletion = useCallback(
    (outputCallId, requestFollowup = true) => {
      mcpStateRef.current.pending = true;
      if (outputCallId) {
        if (mcpStateRef.current.handledCallIds.has(outputCallId)) return;
        mcpStateRef.current.handledCallIds.add(outputCallId);
      }
      if (mcpStateRef.current.pendingCount > 0) mcpStateRef.current.pendingCount -= 1;
      if (requestFollowup && mcpStateRef.current.pendingCount === 0) {
        requestFollowupResponse('mcp_completed', outputCallId);
      }
      resumeMcpIfNeeded(outputCallId);
    },
    [requestFollowupResponse, resumeMcpIfNeeded],
  );

  const scheduleResumeRetry = useCallback(
    (outputCallId) => {
      if (!mcpStateRef.current.pending) return;
      if (mcpStateRef.current.retry.timeoutId) return;
      if (mcpStateRef.current.retry.count >= 2) {
        clearPendingMcp();
        return;
      }
      const attempt = mcpStateRef.current.retry.count + 1;
      const delay = attempt * 600;
      mcpStateRef.current.retry.count = attempt;
      mcpStateRef.current.retry.timeoutId = setTimeout(() => {
        mcpStateRef.current.retry.timeoutId = null;
        if (outputCallId) mcpStateRef.current.resumeRequestedCallIds.delete(outputCallId);
        mcpStateRef.current.resumeRequestedAny = false;
        requestFollowupResponse('mcp_completed', outputCallId);
      }, delay);
    },
    [clearPendingMcp, requestFollowupResponse],
  );

  const sendFunctionCallOutput = useCallback((callId, output) => {
    const channel = dataChannelRef.current;
    if (!callId || !channel || channel.readyState !== 'open') return false;
    channel.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify(output || {}),
        },
      }),
    );
    return true;
  }, []);

  const sendItemRetrieve = useCallback((itemId) => {
    const channel = dataChannelRef.current;
    if (!itemId || !channel || channel.readyState !== 'open') return false;
    channel.send(
      JSON.stringify({
        type: 'conversation.item.retrieve',
        item_id: itemId,
      }),
    );
    return true;
  }, []);

  const sendMcpApproval = useCallback((approvalRequestId, approve = true) => {
    const channel = dataChannelRef.current;
    if (!approvalRequestId || !channel || channel.readyState !== 'open') return false;
    channel.send(
      JSON.stringify({
        type: 'conversation.item.create',
        previous_item_id: approvalRequestId,
        item: {
          type: 'mcp_approval_response',
          approval_request_id: approvalRequestId,
          approve,
        },
      }),
    );
    return true;
  }, []);

  const buildMcpFailureInstructions = useCallback((localeHint) => {
    const normalizedLocale = localeHint || 'en-US';
    return `Respond in the language that matches the locale code "${normalizedLocale}". The requested information could not be retrieved because a data source failed. Apologize briefly and ask if the user wants to try again or ask something else.`;
  }, []);

  const handleMcpFailure = useCallback(
    (callId, localeHint) => {
      const failureKey = callId || 'unknown';
      if (mcpStateRef.current.failedCallIds.has(failureKey)) return;
      mcpStateRef.current.failedCallIds.add(failureKey);
      const instructions = buildMcpFailureInstructions(localeHint);
      requestFollowupResponse('mcp_failed', callId, { instructions });
    },
    [buildMcpFailureInstructions, requestFollowupResponse],
  );

  const handleOpenUrlCalls = useCallback(
    ({ responseId, outputItems }) => {
      const responseOpenUrlItems = Array.isArray(outputItems)
        ? outputItems.filter((item) => item?.type === 'function_call' && item?.name === 'open_url')
        : [];
      responseOpenUrlItems.forEach((item) => {
        const key = getFunctionCallKey(item.call_id || item.id || null, responseId, item.name);
        upsertFunctionCall({
          key,
          name: item.name,
          responseId,
          callId: item.call_id || item.id || null,
          args: item.arguments || null,
        });
      });

      const pendingCalls = [...functionStateRef.current.pendingCalls.values()].filter((call) => {
        if (!call?.name || call.name !== 'open_url') return false;
        if (!responseId) return true;
        return !call.responseId || call.responseId === responseId;
      });

      for (const call of pendingCalls) {
        const callKey = call.key || call.callId || call.args || null;
        if (callKey && functionStateRef.current.handledOpenUrlCalls.has(callKey)) continue;
        const args = safeJsonParse(call.args);
        const allowedUrl = getAllowedOpenUrl(args?.url);
        if (!allowedUrl) {
          if (call.callId) {
            sendFunctionCallOutput(call.callId, {
              error: 'blocked_url',
              allowed_prefix: getAllowedOpenUrlPrefix(),
            });
            requestFollowupResponse('open_url_blocked', call.callId);
          }
          if (call.key) functionStateRef.current.pendingCalls.delete(call.key);
          continue;
        }
        if (callKey) functionStateRef.current.handledOpenUrlCalls.add(callKey);
        if (call.key) functionStateRef.current.pendingCalls.delete(call.key);
        cleanupRealtimeSession(true);
        window.location.assign(allowedUrl);
        return true;
      }
      return false;
    },
    [
      cleanupRealtimeSession,
      getFunctionCallKey,
      requestFollowupResponse,
      safeJsonParse,
      sendFunctionCallOutput,
      upsertFunctionCall,
    ],
  );

  const startSession = useCallback(
    async ({
      assistant_name,
      chainId,
      currency,
      hotelId,
      isChain,
      locale,
      onTranscript,
      setIsConnecting,
      setReady,
    }) => {
      cleanupRealtimeSession();

      const fingerprint = getClientFingerprint();
      const finalizeSession = (error) => {
        const errorMessage = typeof error === 'string' ? error : error?.message;
        if (errorMessage) {
          Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: errorMessage });
        }
        cleanupRealtimeSession(true);
        setReady(false);
        setIsConnecting(false);
      };

      try {
        const sessionResponse = await request({
          endpoint: `/voice/session?fingerprint=${fingerprint}&hotelId=${isChain ? '' : hotelId ?? ''}&chainId=${
            isChain ? chainId ?? '' : ''
          }&locale=${locale ?? ''}&currency=${currency ?? ''}`,
          hostname: process.env.SERVICE_SYNAPSE,
          method: 'GET',
        });

        const ephemeralKey = sessionResponse.value;
        const peerConnection = new RTCPeerConnection();
        peerConnectionRef.current = peerConnection;
        peerConnection.addEventListener('connectionstatechange', () => {
          const { connectionState } = peerConnection;
          if (connectionState === 'failed' || connectionState === 'disconnected' || connectionState === 'closed') {
            finalizeSession();
          }
        });

        audioElementRef.current = document.createElement('audio');
        audioElementRef.current.autoplay = true;
        peerConnection.ontrack = ({ streams }) => {
          const [remoteStream] = streams;
          if (audioElementRef.current) {
            audioElementRef.current.srcObject = remoteStream || null;
          }
          if (!audioContextRef.current || !audioAnalyzersRef.current.outputAnalyser || !remoteStream) return;
          if (outputSourceRef.current) outputSourceRef.current.disconnect();
          outputSourceRef.current = audioContextRef.current.createMediaStreamSource(remoteStream);
          outputSourceRef.current.connect(audioAnalyzersRef.current.outputAnalyser);
        };

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000,
          },
        });

        localStreamRef.current = mediaStream;
        const [track] = mediaStream.getAudioTracks();

        const AudioContextConstructor = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!AudioContextConstructor) throw new Error('AudioContext not supported');
        const audioContext = new AudioContextConstructor();
        audioContextRef.current = audioContext;
        if (audioContext.state === 'suspended') await audioContext.resume();
        const inputAnalyser = audioContext.createAnalyser();
        const outputAnalyser = audioContext.createAnalyser();
        inputAnalyser.fftSize = 1024;
        outputAnalyser.fftSize = 1024;
        audioAnalyzersRef.current.inputAnalyser = inputAnalyser;
        audioAnalyzersRef.current.outputAnalyser = outputAnalyser;
        inputFrequencyDataRef.current = new Uint8Array(inputAnalyser.frequencyBinCount);
        outputFrequencyDataRef.current = new Uint8Array(outputAnalyser.frequencyBinCount);
        audioAnalyzersRef.current.getInputByteFrequencyData = () => {
          if (!audioAnalyzersRef.current.inputAnalyser || !inputFrequencyDataRef.current) return null;
          audioAnalyzersRef.current.inputAnalyser.getByteFrequencyData(inputFrequencyDataRef.current);
          return inputFrequencyDataRef.current;
        };
        audioAnalyzersRef.current.getOutputByteFrequencyData = () => {
          if (!audioAnalyzersRef.current.outputAnalyser || !outputFrequencyDataRef.current) return null;
          audioAnalyzersRef.current.outputAnalyser.getByteFrequencyData(outputFrequencyDataRef.current);
          return outputFrequencyDataRef.current;
        };

        const inputSource = audioContext.createMediaStreamSource(mediaStream);
        inputSourceRef.current = inputSource;
        inputSource.connect(inputAnalyser);

        if (track) {
          localAudioTrackRef.current = track;
          track.enabled = false;
          await track.applyConstraints({
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          });
          peerConnection.addTrack(track, mediaStream);
        }

        const dataChannel = peerConnection.createDataChannel('oai-events');
        dataChannelRef.current = dataChannel;
        dataChannel.addEventListener('close', () => {
          finalizeSession();
        });
        dataChannel.addEventListener('error', (event) => {
          finalizeSession(event);
        });

        dataChannel.addEventListener('open', () => {
          pendingUserTranscriptRef.current = '';
          Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_START });

          const localeHint = locale || 'en-US';
          const introductionMessage = `Respond in the language that matches the locale code "${localeHint}". Introduce yourself using the following sentence, translated into that language: "Hello, I am ${assistant_name}, your virtual assistant. I can help you with information about our services and facilities. What would you like to know today?"`;

          pendingIntroRef.current = true;
          introResponseIdRef.current = null;
          dataChannel.send(
            JSON.stringify({
              type: 'response.create',
              response: {
                instructions: introductionMessage,
              },
            }),
          );
        });

        dataChannel.addEventListener('message', async (event) => {
          const payload = JSON.parse(event.data);
          const { response } = payload;
          const itemId = payload.item?.id || payload.item_id || null;
          const callId = payload.item?.call_id || payload.call_id || itemId;
          const responseId = payload.response_id || response?.id || null;

          if (payload.type === 'response.output_item.added' && payload.item?.type === 'message') {
            if (pendingIntroRef.current && !introResponseIdRef.current && payload.response_id) {
              introResponseIdRef.current = payload.response_id;
            }
            if (responseId) responseStateRef.current.audioResponseIds.add(responseId);
            if (responseId) {
              mcpStateRef.current.responsesWithMessage.add(responseId);
              mcpStateRef.current.responseIds.delete(responseId);
            }
            if (mcpStateRef.current.pending && mcpStateRef.current.pendingCount === 0) {
              if (mcpStateRef.current.resumeRequestedAny) clearPendingFollowup();
              clearPendingMcp();
            }
          }

          if (payload.type === 'output_audio_buffer.stopped' || payload.type === 'output_audio_buffer.cleared') {
            if (responseId) {
              responseStateRef.current.audioResponseIds.delete(responseId);
            } else {
              responseStateRef.current.audioResponseIds.clear();
            }
            if (
              pendingIntroRef.current &&
              introResponseIdRef.current &&
              payload.response_id === introResponseIdRef.current
            ) {
              enableMicrophone();
              pendingIntroRef.current = false;
              introResponseIdRef.current = null;
            }
            sendPendingFollowup();
          }

          if (
            payload.type === 'response.output_item.added' &&
            (payload.item?.type === 'mcp_call' || payload.item?.type === 'mcp_tool_call')
          ) {
            if (responseId) mcpStateRef.current.responseIds.add(responseId);
            trackMcpStart(callId);
          }

          if (payload.type === 'response.output_item.added' && payload.item?.type === 'function_call') {
            const name = payload.item?.name || payload.name || null;
            const key = getFunctionCallKey(callId, responseId, name);
            upsertFunctionCall({ key, name, responseId, callId, args: payload.item?.arguments || null });
          }

          if (payload.type === 'response.function_call_arguments.done') {
            const name = payload.name || null;
            const key = getFunctionCallKey(payload.call_id || null, responseId, name);
            upsertFunctionCall({ key, name, responseId, callId: payload.call_id || null, args: payload.arguments });
          }

          if (payload.type === 'response.mcp_call.in_progress') {
            trackMcpStart(callId);
            if (payload.item_id) sendItemRetrieve(payload.item_id);
          }

          if (payload.type === 'response.mcp_tool_call.in_progress') {
            trackMcpStart(callId);
            if (payload.item_id) sendItemRetrieve(payload.item_id);
          }

          if (payload.type === 'mcp_list_tools.in_progress' || payload.type === 'response.mcp_list_tools.in_progress') {
            if (payload.item_id) sendItemRetrieve(payload.item_id);
          }

          if (
            payload.type === 'response.output_item.done' &&
            (payload.item?.type === 'mcp_call' || payload.item?.type === 'mcp_tool_call')
          ) {
            if (payload.item?.error || payload.item?.status === 'failed') {
              handleMcpFailure(callId, locale);
            }
            handleMcpCompletion(callId);
          }

          if (
            payload.type === 'response.mcp_call.completed' ||
            payload.type === 'response.mcp_call.failed' ||
            payload.type === 'response.mcp_tool_call.completed' ||
            payload.type === 'response.mcp_tool_call.failed'
          ) {
            if (payload.type === 'response.mcp_call.failed' || payload.type === 'response.mcp_tool_call.failed') {
              handleMcpFailure(callId, locale);
            }
            handleMcpCompletion(callId);
          }

          if (payload.type === 'conversation.item.added' || payload.type === 'conversation.item.retrieved') {
            if (payload.item?.type === 'mcp_call' || payload.item?.type === 'mcp_tool_call') {
              trackMcpStart(callId);
            }
            if (payload.item?.type === 'mcp_approval_request') {
              const approvalId = payload.item?.id || payload.item_id || null;
              if (approvalId && !mcpStateRef.current.handledApprovalIds.has(approvalId)) {
                mcpStateRef.current.handledApprovalIds.add(approvalId);
                sendMcpApproval(approvalId, true);
              }
            }
          }

          if (payload.type === 'conversation.item.done') {
            if (payload.item?.type === 'mcp_call' || payload.item?.type === 'mcp_tool_call') {
              if (payload.item?.status === 'failed' || payload.item?.error) {
                handleMcpFailure(callId, locale);
              }
              handleMcpCompletion(callId);
            }
            if (payload.item?.type === 'mcp_approval_request') {
              const approvalId = payload.item?.id || payload.item_id || null;
              if (approvalId && !mcpStateRef.current.handledApprovalIds.has(approvalId)) {
                mcpStateRef.current.handledApprovalIds.add(approvalId);
                sendMcpApproval(approvalId, true);
              }
            }
          }

          if (payload.type === 'response.done') {
            if (responseId) mcpStateRef.current.responsesDone.add(responseId);
            const outputItems = Array.isArray(response?.output) ? response.output : [];
            const hasMessage = outputItems.some((item) => item?.type === 'message');
            const status = response?.status;
            const reason = response?.status_details?.reason || response?.status_details?.error?.code || null;
            if (status !== 'completed' && responseId) {
              responseStateRef.current.audioResponseIds.delete(responseId);
            }
            if (status === 'completed') {
              if (
                hasMessage &&
                mcpStateRef.current.pending &&
                mcpStateRef.current.pendingCount === 0 &&
                mcpStateRef.current.responseIds.size === 0
              ) {
                clearPendingMcp();
              }
            } else if (mcpStateRef.current.pending) {
              if (status === 'failed') {
                if (mcpStateRef.current.failedCallIds.size === 0) {
                  scheduleResumeRetry(callId);
                }
              } else if (status === 'cancelled' && reason !== 'turn_detected') {
                if (mcpStateRef.current.failedCallIds.size === 0) {
                  scheduleResumeRetry(callId);
                }
              }
            }
            resumeMcpIfNeeded(callId);
            handleOpenUrlCalls({ responseId, outputItems });
          }

          if (payload.type === 'response.created') {
            if (responseId) responseStateRef.current.activeId = responseId;
          }

          if (payload.type === 'conversation.item.input_audio_transcription.completed') {
            const transcript = payload.transcript?.trim();
            if (transcript) pendingUserTranscriptRef.current += transcript;
          }

          if (payload.type === 'response.done' && responseId && responseStateRef.current.activeId === responseId) {
            responseStateRef.current.activeId = null;
            sendPendingFollowup();
          }

          if (payload.type === 'response.output_audio_transcript.done') {
            const transcript = payload.transcript?.trim();
            if (!transcript || !pendingUserTranscriptRef.current) return;

            const input = pendingUserTranscriptRef.current.trim();
            pendingUserTranscriptRef.current = '';

            const activeLocale = locale || 'en-US';

            const channelId = hotelId || chainId;
            if (channelId && onTranscript) {
              onTranscript({ author: 'user', body: input });
              onTranscript({ author: 'ai', body: transcript });
            }

            Event.publish(EVENT.METRICS, {
              id: 'SYNAPSE_RESPONSE',
              fingerprint,
              text: transcript,
              input,
              agent: 'aura',
              locale: activeLocale,
            });
          }
        });

        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);

        const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
          method: 'POST',
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            'Content-Type': 'application/sdp',
          },
        });

        if (!sdpResponse.ok) throw new Error('Failed to establish OpenAI Realtime session');

        const answer = { type: 'answer', sdp: await sdpResponse.text() };

        await peerConnection.setRemoteDescription(answer);
        setReady(true);
      } catch (error) {
        Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: error?.message });
        setReady(false);
        throw error;
      } finally {
        setIsConnecting(false);
      }
    },
    [
      cleanupRealtimeSession,
      clearPendingFollowup,
      clearPendingMcp,
      enableMicrophone,
      handleMcpCompletion,
      handleMcpFailure,
      handleOpenUrlCalls,
      resumeMcpIfNeeded,
      scheduleResumeRetry,
      sendPendingFollowup,
      sendItemRetrieve,
      sendMcpApproval,
      trackMcpStart,
      upsertFunctionCall,
      getFunctionCallKey,
    ],
  );

  const endSession = useCallback(() => {
    cleanupRealtimeSession(true);
  }, [cleanupRealtimeSession]);

  return { startSession, endSession, audioAnalyzers: audioAnalyzersRef.current };
};

export { useOpenAiRealtimeSession };
